import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable, throwError } from "rxjs";
import { catchError } from "rxjs/operators";
import { environment } from "src/environments/environment";
import { APIResponse } from "../../../models/Response";
import { Project } from "../../../models/project";
import { Organization } from "../../../models/organization";
import { AllTeamOfUser } from "../../../models/team-members";
import { TeamNameOrgName } from "../../../models/custom-models/team-name-org-name";

export type convertedOrgAndTeamsIdResponse = {
    mapOfOrgAndTeams: any;
    orgs: any;
    teams: any;
};

@Injectable({
    providedIn: "root",
})
export class OrganizationService {
    apiServerUrl = environment.apiBaseUrl;

    constructor(private http: HttpClient) {}

    // get organization list for user by token
    getUserOrganizationList(): Observable<APIResponse<Organization[]>> {
        return this.http
            .get<APIResponse<Organization[]>>(`${this.apiServerUrl}/organization/userAllOrganizationList`)
            .pipe(
                catchError((error) => {
                    return throwError(error);
                }),
            );
    }

    //  get accountId from userAccount table for given orgId and email
    getOwnerAccountId(email: string, orgId: number): Observable<any> {
        return this.http.get(`${this.apiServerUrl}/organization/ownerAccountId/${email}/${orgId}`).pipe(
            catchError((error) => {
                return throwError(error);
            }),
        );
    }

    //  get list of projects from project table
    getProjectList(orgId: any): Observable<APIResponse<Project[]>> {
        return this.http.get<APIResponse<Project[]>>(`${this.apiServerUrl}/project/projectListByOrgId/${orgId}`).pipe(
            catchError((error) => {
                return throwError(error);
            }),
        );
    }

    convertTeamsIntoOrgAndTeamMap(allTeams: AllTeamOfUser[]): convertedOrgAndTeamsIdResponse {
        let orgIdAndTeamsMap: Map<string, TeamNameOrgName[]> = new Map<string, TeamNameOrgName[]>();
        let teams = JSON.parse(JSON.stringify(allTeams));
        const orgMap = new Map();
        teams.forEach((item: AllTeamOfUser) => {
            if (!orgMap.has(item.orgId)) {
                orgMap.set(item.orgId, { orgId: item.orgId, organizationName: item.orgName });
            }
        });
        let orgs = Array.from(orgMap.values());
        orgs.forEach((organization: Organization) => {
            let orgTeamsKeyValue = teams.filter((team: TeamNameOrgName) => {
                return team.orgName == organization.organizationName;
            });
            orgIdAndTeamsMap.set(organization?.orgId, orgTeamsKeyValue);
        });

        let returnResponse: convertedOrgAndTeamsIdResponse = {} as convertedOrgAndTeamsIdResponse;
        returnResponse.teams = teams;
        returnResponse.orgs = orgs;
        returnResponse.mapOfOrgAndTeams = orgIdAndTeamsMap;
        return returnResponse;
    }

    convertTeamsIntoOrgAndProjectsMap(allTeams: AllTeamOfUser[]): Map<number, AllTeamOfUser[]> {
        let orgIdAndProjectsMap: Map<number, AllTeamOfUser[]> = new Map();
        allTeams.forEach((team: AllTeamOfUser) => {
            // Check if the orgId already exists in the map
            if (!orgIdAndProjectsMap.has(team.orgId)) {
                // If it doesn't exist, initialize with the current team
                orgIdAndProjectsMap.set(team.orgId, [team]);
            } else {
                // If it exists, add the current team only if its project is not already included
                let existingTeams = orgIdAndProjectsMap.get(team.orgId);
                if (existingTeams && !existingTeams.some((existingTeam) => existingTeam.projectId === team.projectId)) {
                    existingTeams.push(team);
                    orgIdAndProjectsMap.set(team.orgId, existingTeams);
                }
            }
        });
        return orgIdAndProjectsMap;
    }

    convertTeamsIntoProjectsAndTeamsMap(allTeams: AllTeamOfUser[], orgId: number): Map<number, AllTeamOfUser[]> {
        let projectIdAndTeamsMap: Map<number, AllTeamOfUser[]> = new Map();
        allTeams?.forEach((team: AllTeamOfUser) => {
            // Check if the team belongs to the specified orgId
            if (team.orgId === orgId) {
                // Proceed only if the team is part of the specified organization
                if (!projectIdAndTeamsMap.has(team.projectId)) {
                    projectIdAndTeamsMap.set(team.projectId, [team]);
                } else {
                    let teamsForProject = projectIdAndTeamsMap.get(team.projectId);
                    if (teamsForProject && !teamsForProject.some((t) => t.teamId === team.teamId)) {
                        teamsForProject.push(team);
                        projectIdAndTeamsMap.set(team.projectId, teamsForProject);
                    }
                }
            }
        });
        return projectIdAndTeamsMap;
    }

    convertTeamsToUniqueOrganizations(allTeams: AllTeamOfUser[]): Organization[] {
        let teams = JSON.parse(JSON.stringify(allTeams)) as AllTeamOfUser[];
        const orgMap = new Map();
        teams.forEach((item: AllTeamOfUser) => {
            if (!orgMap.has(item.orgId)) {
                orgMap.set(item.orgId, { orgId: item.orgId, organizationName: item.orgName });
            }
        });
        return Array.from(orgMap.values());
    }

    convertIntoUniqueProjects(allTeams: AllTeamOfUser[]): AllTeamOfUser[] {
        let teams = JSON.parse(JSON.stringify(allTeams)) as AllTeamOfUser[];
        // unique projects and must not have project id 0
        let uniqueProjects: Map<number, AllTeamOfUser> = new Map();
        teams.forEach((team: AllTeamOfUser) => {
            if (!uniqueProjects.has(team.projectId) && team.projectId != 0) {
                uniqueProjects.set(team.projectId, team);
            }
        });
        return Array.from(uniqueProjects.values());
    }

    getOrgAllBU(orgId: number) {
        return this.http.get(`${this.apiServerUrl}/organization/getOrgAllBU/${orgId}`).pipe(
            catchError((error) => {
                return throwError(error);
            }),
        );
    }

    requestOrganizationDeletion(orgId: number, reason: string, accountId: number): Observable<APIResponse<any>> {
        const headers = new HttpHeaders().set("accountIds", String(accountId));
        return this.http
            .post<APIResponse<any>>(
                `${this.apiServerUrl}/organization/requestDeletion`,
                { orgId, reason },
                { headers },
            )
            .pipe(
                catchError((error) => {
                    return throwError(error);
                }),
            );
    }

    filterPersonalTasksTeamFromAllTeams(data: AllTeamOfUser[]) {
        // check for if any team exist with id 0 and that team org id is also zero .
        // if exist then remove that team from the list
        if(!data || data.length === 0) return [];
        try {
            let teams = JSON.parse(JSON.stringify(data)) as AllTeamOfUser[];
            let personalTeam = teams.find((team) => team.teamId === 0 && team.orgId === 0);
            if (personalTeam) {
                return teams.filter((team) => team.teamId !== 0);
            }
            return teams;
        } catch (error) {
            return data;
        }
    }
}
